import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import DialCodeSelect from './DialCodeSelect';
import { getDetectedCountry, getCountryList, submitLead } from '../services/api';

const RegistrationModal = ({ isOpen, onClose, initialType = 'registration', packageTitle = '', packageAmount = 0 }) => {
  const countries = getCountryList();

  const [formData, setFormData] = useState({
    name: '',
    country: 'US',
    countryDialCode: 1,
    phone: '',
    email: '',
    message: '',
  });

  const [detectedCountryName, setDetectedCountryName] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [geoData, setGeoData] = useState(null);
  const [rawGeo, setRawGeo] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      setErrors({});
      setApiError('');
      return;
    }

    document.body.style.overflow = 'hidden';

    // Fetch geo on open
    let cancelled = false;
    (async () => {
      const detected = await getDetectedCountry();
      if (cancelled) return;
      setDetectedCountryName(detected.countryName);
      setGeoData(detected.geoData);
      setRawGeo(detected.rawGeo);
      setFormData(prev => ({
        ...prev,
        country: detected.countryCode,
        countryDialCode: detected.dialCode,
      }));
    })();

    return () => {
      cancelled = true;
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ─── Validation helpers ─────────────────────────────────────────────
  const validateEmail = (email) => {
    const t = email.trim();
    if (!t) return false;
    if ((t.match(/@/g) || []).length !== 1) return false;
    const [local, domain] = t.split('@');
    if (!local || !domain || !domain.includes('.')) return false;
    if (domain.startsWith('.') || domain.endsWith('.')) return false;
    if (t.includes(' ') || t.includes('..')) return false;
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(t);
  };

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone)) e.phone = 'Phone number must be exactly 10 digits';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!validateEmail(formData.email)) e.email = 'Please enter a valid email address';
    if (!agreeToTerms) e.terms = 'You must agree to the Terms & Conditions';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: value.replace(/\D/g, '').substring(0, 10) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCountryChange = (e) => {
    const dialCode = Number(e.target.value);
    const match = countries.find(c => c.dialCode === dialCode);
    setFormData(prev => ({
      ...prev,
      countryDialCode: dialCode,
      country: match ? match.code : prev.country,
    }));
  };

  // ─── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSubmitting(true);

    // Analytics
    if (window.gtag) window.gtag('event', 'conversion', { send_to: 'AW-CONVERSION_ID/LABEL', event_category: initialType });
    if (window.fbq) window.fbq('track', 'Lead', { content_name: initialType });

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phone,
      countryDialCode: formData.countryDialCode,
      country: formData.country,
      description: formData.message,
      packageTitle,
      packageAmount,
      geoData,
      rawGeo,
    });

    setIsSubmitting(false);

    if (result.success) {
      window.location.href = result.redirectUrl;
    } else {
      setApiError(result.error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="registration-modal">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl transform transition-all overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 bg-white rounded-full p-2 shadow-lg" data-testid="modal-close-btn">
          <X className="w-5 h-5" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 h-full">
          {/* Left — branded panel */}
          <div className="hidden md:block relative overflow-hidden">
            <img src="https://images.unsplash.com/photo-1771964427768-1f8ae7794358?w=600&h=800&fit=crop&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#FF035B]/15 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#FF035B]/10 rounded-full blur-2xl"></div>

            <div className="relative z-10 h-full flex flex-col justify-between p-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                  <svg className="w-6 h-6 text-[#FF035B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                </div>
                <span className="text-white/60 text-xs font-medium tracking-wider uppercase">Brand Protection</span>
              </div>

              <div className="flex-1 flex flex-col justify-center">
                <h3 className="text-3xl font-bold text-white leading-tight mb-4">
                  Secure Your<br />
                  <span className="bg-gradient-to-r from-[#FF035B] to-[#ff6b9d] bg-clip-text text-transparent">Brand Identity</span><br />
                  Today
                </h3>
                <p className="text-white/70 text-sm leading-relaxed mb-6">Join 10,000+ businesses who trust us to protect their trademarks with expert legal guidance.</p>
                <div className="space-y-3">
                  {['Free Expert Consultation', 'Starting at $45 + Filing Fees', '98% Success Rate'].map((t, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#FF035B]/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-[#FF035B]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      </div>
                      <span className="text-white/80 text-xs">{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  ))}
                </div>
                <p className="text-white/50 text-xs">Rated 4.9/5 by 2,500+ clients</p>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="p-6 flex flex-col justify-center text-left">
            <div className="mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Start Your Trademark Registration</h2>
              <p className="text-gray-600 text-xs">Fill out the form below and our trademark experts will contact you.</p>
            </div>

            {apiError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="modal-api-error">
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-3" data-testid="registration-form">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Your Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900" placeholder="John Doe" data-testid="modal-name-input" />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Your Email *</label>
                <input type="text" name="email" value={formData.email} onChange={handleChange} autoComplete="email" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900" placeholder="john@example.com" data-testid="modal-email-input" />
                {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="modal-email-error">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Number (with country code) *</label>
                <div className="flex gap-2">
                  <DialCodeSelect
                    value={formData.countryDialCode}
                    onChange={(dialCode) => handleCountryChange({ target: { value: dialCode } })}
                    countries={countries}
                    testId="modal-dialcode-select"
                  />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }} maxLength="10" inputMode="numeric" pattern="[0-9]*" className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900" placeholder="1234567890" data-testid="modal-phone-input" />
                </div>
                <p className="text-xs text-gray-500 mt-1">{formData.phone.length}/10 digits</p>
                {errors.phone && <p className="text-red-500 text-xs mt-1" data-testid="modal-phone-error">{errors.phone}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tell us about your brand (Optional)</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows="2" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" data-testid="modal-message-input" />
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2">
                <input type="checkbox" id="agreeToTerms" checked={agreeToTerms} onChange={(e) => { setAgreeToTerms(e.target.checked); if (errors.terms) setErrors(prev => ({ ...prev, terms: '' })); }} className="w-4 h-4 mt-0.5 text-[#FF035B] border-gray-300 rounded focus:ring-[#FF035B] cursor-pointer" data-testid="modal-terms-checkbox" />
                <label htmlFor="agreeToTerms" className="text-xs text-gray-700 cursor-pointer">
                  I agree to the <a href="/terms-conditions" target="_blank" className="text-[#FF035B] hover:underline font-semibold">Terms & Conditions</a> and <a href="/privacy-policy" target="_blank" className="text-[#FF035B] hover:underline font-semibold">Privacy Policy</a>
                </label>
              </div>
              {errors.terms && <p className="text-red-500 text-xs -mt-2">{errors.terms}</p>}

              {/* Submit */}
              <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold px-6 py-3 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none" data-testid="modal-submit-btn">
                {isSubmitting ? 'Submitting...' : (<><Send className="w-4 h-4" />Start Registration</>)}
              </button>

              <p className="text-center text-gray-500 text-xs">No credit card required &bull; Free consultation</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
